import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import mainStore from 'src/stores/main';
import { useConfig } from 'src/hooks/config';
import Money from 'src/common/components/money';
import { getBetCalculations } from 'calculation';
import upperDataStore from 'src/stores/upperData';
import selectionsStore from 'src/stores/selections';
import { useTranslation } from "src/hooks/translation";
import { BetCalculationsEnum } from 'src/constants/enums/betCalculations';
import { BetTypeEnum } from 'src/constants/enums/betType';
import betBuilderStore from 'src/stores/betBuilder';
import { getEffectiveSelectionsForCalc, hasBetBuilderSelections } from 'src/common/helpers/betBuilder';

interface IProps {
  stake: number;
}

const BetDetailsTotalWin = ({
  stake
}: IProps) => {
  const t = useTranslation();
  const config = useConfig();
  const { partnerConfig, currencyConfig } = useRecoilValue(upperDataStore);
  const { selections } = useRecoilValue(selectionsStore);
  const { betType, profitBoostPercent, systemCombinationCount, isProfitBoost } = useRecoilValue(mainStore);
  const { bonusRules } = useRecoilValue(upperDataStore);
  const { groups: betBuilderGroups } = useRecoilValue(betBuilderStore);

  const filtredBonusRules = bonusRules.filter(item => !item.ExactLosing);

  // BB cards collapse to one synthetic leg each so bonus rules and tax apply
  // via the standard calc pipeline. Force Multiple type when BB is present.
  const hasBb = hasBetBuilderSelections(selections);
  const effectiveSelections = hasBb
    ? getEffectiveSelectionsForCalc(selections, betBuilderGroups)
    : selections;
  const calcBetType = hasBb ? BetTypeEnum.Multiple : betType;

  const { totalWin } = useMemo(() => {
    if (effectiveSelections === null) return { totalWin: 0 };

    return getBetCalculations([BetCalculationsEnum.TotalWin], {
      config,
      partnerConfig,
      events: effectiveSelections,
      stake: +stake,
      betType: calcBetType,
      bonusRule: filtredBonusRules,
      currencyConfig,
    });
  }, [config, partnerConfig, effectiveSelections, currencyConfig, calcBetType, stake, filtredBonusRules]);

  const { possibleWin } = useMemo(() => {
    if (effectiveSelections === null) return { possibleWin: 0 };

    return getBetCalculations([BetCalculationsEnum.PossibleWin], {
      config,
      partnerConfig,
      betType: calcBetType,
      stake: +stake,
      events: effectiveSelections,
      systemCombinationCount,
      currencyConfig,
    });
  }, [config, partnerConfig, currencyConfig, stake, calcBetType, effectiveSelections, systemCombinationCount]);


  if (!totalWin || betType === BetTypeEnum.System) return null;

  const profitBoost = profitBoostPercent && ((possibleWin - stake) * profitBoostPercent) / 100;
  const totalWins = profitBoost && isProfitBoost ? profitBoost + totalWin : totalWin;

  return (
    <div className="B-betslip-bet-details-info-row">
      <p className="B-info-row-title">{t('Total win:')}</p>
      <p className="B-info-row-number"><Money amount={totalWins} /></p>
    </div>
  );
};

export default BetDetailsTotalWin;
